import {
  BarChartOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  MoreOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Statistic, Typography, theme } from 'antd';
import type { CSSProperties } from 'react';
import type { ResultadoVisita, VisitaStatsResponse } from '@aid/shared';
import { DashboardCard } from '../../../components/DashboardCard';
import './visitas-kpis.css';

const { Text } = Typography;

type KpiIcon = typeof SearchOutlined;

interface ResultKpiMeta {
  icon: KpiIcon;
  tone: 'warning' | 'info' | 'primary' | 'success' | 'neutral';
}

const RESULT_KPI_META: Record<ResultadoVisita, ResultKpiMeta> = {
  NO_ENCONTRADO: { icon: SearchOutlined, tone: 'warning' },
  VOLVERA: { icon: FieldTimeOutlined, tone: 'info' },
  DEPOSITARA: { icon: WalletOutlined, tone: 'primary' },
  PAGO: { icon: CheckCircleOutlined, tone: 'success' },
  OTRO: { icon: MoreOutlined, tone: 'neutral' },
};

type KpiToneToken =
  'colorWarning' | 'colorInfo' | 'colorPrimary' | 'colorSuccess' | 'colorTextSecondary';

interface VisitasKpiSummaryProps {
  stats?: VisitaStatsResponse;
  isLoading: boolean;
  resultKeys: ResultadoVisita[];
  caption?: string;
  translate: (key: string) => string;
}

interface VisitasKpiCardProps {
  label: string;
  value: number;
  icon: KpiIcon;
  tone: ResultKpiMeta['tone'];
  total?: number;
  caption?: string;
  empty: boolean;
  primary?: boolean;
  distribution?: { label: string; value: number; color: string }[];
}

const TONE_TOKENS: Record<ResultKpiMeta['tone'], KpiToneToken> = {
  warning: 'colorWarning',
  info: 'colorInfo',
  primary: 'colorPrimary',
  success: 'colorSuccess',
  neutral: 'colorTextSecondary',
};

function VisitasKpiCard({
  label,
  value,
  icon: Icon,
  tone,
  total,
  caption,
  empty,
  primary = false,
  distribution,
}: VisitasKpiCardProps) {
  const { token } = theme.useToken();
  const accent = token[TONE_TOKENS[tone]];
  const ratio = total && total > 0 ? Math.min((value / total) * 100, 100) : 0;
  const distributionTotal = distribution?.reduce((sum, segment) => sum + segment.value, 0) ?? 0;
  const valueLabel = empty ? '—' : value.toLocaleString();
  const style = { '--visitas-kpi-accent': accent } as CSSProperties;

  return (
    <DashboardCard
      className={`visitas-kpi-card${primary ? ' visitas-kpi-card--primary' : ''}`}
      style={style}
      bodyStyle={{ padding: primary ? '18px 22px 20px' : '16px 18px 18px' }}
    >
      <div
        className="visitas-kpi-card__content"
        role="group"
        aria-label={`${label}: ${valueLabel}`}
        aria-busy={empty}
      >
        <div className="visitas-kpi-card__label">
          <span className="visitas-kpi-card__icon" aria-hidden="true">
            <Icon />
          </span>
          <Text>{label}</Text>
        </div>
        <Statistic
          value={empty ? '—' : value}
          styles={{
            content: {
              color: empty ? token.colorTextQuaternary : token.colorText,
            },
          }}
          className="visitas-kpi-card__value"
        />
        {distribution && distributionTotal > 0 && !empty ? (
          <div
            className="visitas-kpi-card__meter visitas-kpi-card__meter--distribution"
            role="img"
            aria-label={distribution
              .map((segment) => `${segment.label}: ${segment.value.toLocaleString()}`)
              .join(', ')}
          >
            {distribution.map((segment) => (
              <span
                key={segment.label}
                aria-hidden="true"
                style={{
                  width: `${(segment.value / distributionTotal) * 100}%`,
                  background: segment.color,
                }}
              />
            ))}
          </div>
        ) : total !== undefined && !empty ? (
          <div
            className="visitas-kpi-card__meter"
            role="meter"
            aria-label={`${label}: ${valueLabel} de ${total.toLocaleString()}`}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={value}
          >
            <span style={{ width: `${ratio}%` }} />
          </div>
        ) : null}
        {caption && (
          <Text type="secondary" className="visitas-kpi-card__caption">
            {caption}
          </Text>
        )}
      </div>
    </DashboardCard>
  );
}

export function VisitasKpiSummary({
  stats,
  isLoading,
  resultKeys,
  caption,
  translate,
}: VisitasKpiSummaryProps) {
  const { token } = theme.useToken();
  const total = stats?.total ?? 0;
  const distribution = resultKeys.map((result) => ({
    label: translate(`pages.visitas.resultados.${result}`),
    value: stats?.porResultado[result] ?? 0,
    color: token[TONE_TOKENS[RESULT_KPI_META[result].tone]],
  }));

  return (
    <section className="visitas-kpi-summary" aria-label={translate('pages.visitas.kpis.total')}>
      <div className="visitas-kpi-grid">
        <VisitasKpiCard
          label={translate('pages.visitas.kpis.total')}
          value={total}
          icon={BarChartOutlined}
          tone="primary"
          caption={caption}
          empty={isLoading && !stats}
          primary
          distribution={distribution}
        />
        {resultKeys.map((result) => {
          const meta = RESULT_KPI_META[result];
          return (
            <VisitasKpiCard
              key={result}
              label={translate(`pages.visitas.resultados.${result}`)}
              value={stats?.porResultado[result] ?? 0}
              icon={meta.icon}
              tone={meta.tone}
              total={total}
              caption={caption}
              empty={isLoading && !stats}
            />
          );
        })}
      </div>
    </section>
  );
}
